import React, { useEffect, useState, useRef } from "react";
import "./BlogPage.scss";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
    getBlogDetailById,
    getFeaturedBlogsList,
    getBlogsList,
} from "services/home/blogs";
import { FaArrowDown } from "react-icons/fa";

const BlogDetail = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const blogDetailRef = useRef(null);

    const [blog, setBlog] = useState(location.state?.blog || null);
    const [featuredBlogs, setFeaturedBlogs] = useState([]);
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentSlide, setCurrentSlide] = useState(0);

    // -------------------------------
    // 🔵 SLUGIFY FUNCTION
    // -------------------------------
    const slugify = (text) =>
        text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, "-")
            .replace(/[^\w-]+/g, "")
            .replace(/--+/g, "-");

    // -------------------------------
    // 🔵 FETCH BLOG DETAIL — ID BASED
    // -------------------------------
    const fetchBlogDetail = async (blogId) => {
        try {
            setLoading(true);
            const res = await getBlogDetailById({ id: blogId });

            if (res.code === 200 && res.data) {
                setBlog(res.data);
            }
        } catch (err) {
            console.error("Error fetching blog detail:", err);
        } finally {
            setLoading(false);
        }
    };

    // -------------------------------
    // 🔵 FETCH FEATURED BLOGS
    // -------------------------------
    const fetchFeaturedBlogs = async () => {
        try {
            const res = await getFeaturedBlogsList();
            if (res.code === 200 && res.data?.blogs) setFeaturedBlogs(res.data.blogs);
        } catch (err) {
            console.error("Error fetching featured blogs:", err);
        }
    };

    // -------------------------------
    // 🔵 FETCH LATEST BLOGS
    // -------------------------------
    const fetchBlogs = async () => {
        try {
            const res = await getBlogsList({ page: 1, limit: 5 });
            if (res.code === 200 && res.data?.blogs) setBlogs(res.data.blogs);
        } catch (err) {
            console.error("Error fetching blogs:", err);
        }
    };

    // -------------------------------
    // 🔵 USEEFFECT — Slug → ID matching
    // -------------------------------
    useEffect(() => {
        if (!id) return;

        const fetchBySlug = async () => {
            const res = await getBlogsList({ page: 1, limit: 200 });

            if (res.code === 200 && res.data?.blogs) {
                const matched = res.data.blogs.find(
                    (item) => slugify(item.Title) === id
                );

                if (matched) {
                    fetchBlogDetail(matched.ID);
                }
            }
        };

        fetchBySlug();
        fetchFeaturedBlogs();
        fetchBlogs();
    }, [id]);

    // -------------------------------
    // 🔵 AUTO SLIDER
    // -------------------------------
    useEffect(() => {
        if (blogs.length === 0) return;
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % blogs.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [blogs.length]);

    // -------------------------------
    // 🔵 SCROLL DOWN BUTTON
    // -------------------------------
    const handleScrollDown = () => {
        if (blogDetailRef.current) {
            blogDetailRef.current.scrollBy({
                top: 250,
                behavior: "smooth",
            });
        }
    };

    if (loading)
        return (
            <div className="loader-wrapper">
                <div className="loader"></div>
            </div>
        );

    if (!blog)
        return (
            <div className="loader-wrapper">
                <div>No blog found.</div>
            </div>
        );

    return (
        <div className="blocks-page">
            {/* Banner */}
            <div className="banner">
                <h1 className="banner-title">
                    Power Your Prep with Insightful Blog Articles
                </h1>
                <p className="banner-subtitle">
                    Discover Winning Strategies in Our Blog Series
                </p>
            </div>

            {/* Main Content */}
            <div className="content">
                {/* Left Section */}
                <div className="content-left">
                    <div className="blog-detail" ref={blogDetailRef}>
                        <h2 className="blog-detail-title">{blog.Title}</h2>
                        <p className="blog-detail-date">
                            Published on:{" "}
                            {blog.PublishedDate
                                ? new Date(blog.PublishedDate).toLocaleDateString()
                                : "N/A"}
                        </p>

                        <img
                            src={blog.attachmentUrl}
                            alt={blog.Title}
                            className="blog-detail-image"
                        />

                        <p className="blog-detail-shortdesc">
                            {blog.ShortDescription}
                        </p>

                        <div
                            className="blog-detail-description"
                            dangerouslySetInnerHTML={{ __html: blog.Description }}
                        ></div>

                        <button
                            className="scroll-down-btn"
                            onClick={handleScrollDown}
                            title="Scroll down"
                        >
                            <FaArrowDown />
                        </button>
                    </div>
                </div>

                {/* Right Section */}
                <div className="content-right">
                    {/* Featured Blogs */}
                    <div className="featuredposts">
                        <h4 className="featuredposts-heading">Featured Blogs</h4>
                        <hr />

                        {featuredBlogs.length > 0 ? (
                            featuredBlogs.slice(0, 4).map((fblog) => (
                                <div
                                    key={fblog.ID}
                                    className="featuredpost"
                                    style={{
                                        display: "flex",
                                        gap: "12px",
                                        cursor: "pointer",
                                    }}
                                    onClick={() =>
                                        navigate(`/blog/${slugify(fblog.Title)}`, {
                                            state: { blog: fblog },
                                        })
                                    }
                                >
                                    <img
                                        src={fblog.attachmentUrl}
                                        alt={fblog.Title}
                                        className="w-20 h-20 object-cover rounded-md"
                                    />
                                    <div className="featuredpost-text">
                                        <p className="featuredpost-text-heading">
                                            {fblog.Title}
                                        </p>
                                        <p className="featuredpost-text-content">
                                            {fblog.ShortDescription}
                                        </p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="no-featured-blog-text text-center py-4">
                                No featured blogs available at this time.
                            </p>
                        )}
                    </div>

                    {/* Latest Posts */}
                    <div className="latest-posts">
                        <h4 className="latest-posts-heading">Latest Posts</h4>
                        <hr />
                        <div
                            className="latest-post-slide"
                            onClick={() =>
                                navigate(`/blog/${slugify(blogs[currentSlide].Title)}`, {
                                    state: { blog: blogs[currentSlide] },
                                })
                            }
                        >
                            <img
                                src={blogs[currentSlide]?.attachmentUrl}
                                alt={blogs[currentSlide]?.Title}
                                className="latest-post-image"
                            />
                            <div className="latest-post-details">
                                <h5 className="latest-post-title">
                                    {blogs[currentSlide]?.Title}
                                </h5>
                                <p className="latest-post-content">
                                    {blogs[currentSlide]?.ShortDescription}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Join Community */}
                    <div className="join-community">
                        <h4 className="sidebar-heading">Join the Community</h4>
                        <hr />
                        <h5>Sahyog Care</h5>
                        <a
                            href="#"
                            onClick={(e) => e.preventDefault()}
                            target="_blank"
                            className="telegram-btn"
                            rel="noopener noreferrer"
                        >
                            <div className="icon-circle">
                                <img
                                    src="https://upload.wikimedia.org/wikipedia/commons/8/82/Telegram_logo.svg"
                                    alt="Telegram"
                                />
                            </div>
                            Join our Telegram Channel
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogDetail;
